import { ArdoqId } from '@ardoq/api-types';
import {
  ByIdMetamodelTypePayload,
  DecomposedMetamodel,
  EditMetamodelTypePayload,
  MetamodelEditorState,
  MetamodelType,
} from './types';
import { ROOT_ID, getDescendants, validateNameFromRecord } from './utils';
import { getStandard } from '@ardoq/icons';
import { currentTimestamp } from '@ardoq/date-time';
import { isEqual } from 'lodash';
import { MetamodelEditorLabels } from '../labels';

const DEFAULT_STANDARD = 'custom';

export const handleEditMetamodelType = (
  state: MetamodelEditorState,
  payload: EditMetamodelTypePayload & {
    validationLabels: Pick<
      MetamodelEditorLabels,
      'validationNameEmpty' | 'validationNameTaken'
    >;
  }
): MetamodelEditorState => {
  // creating newState because you cannot re-assign to a parameter of a function
  let newState = { ...state };
  const {
    decomposedMetamodel: { childrenIndexes, metamodelTypes },
  } = newState;

  if (payload.name !== undefined) {
    try {
      validateNameFromRecord(
        metamodelTypes,
        {
          name: payload.name,
          id: payload.id,
        },
        payload.validationLabels
      );
      newState = {
        ...newState,
        editorError: null,
      };
    } catch (e) {
      newState = {
        ...newState,
        editorError: e as Error,
      };
    }
  }
  const newMetamodelType = updateMetamodelTypeFromAPayload(
    metamodelTypes[payload.id],
    payload
  );

  const newDecomposedMetamodel = {
    childrenIndexes: childrenIndexes,
    metamodelTypes: {
      ...metamodelTypes,
      [payload.id]: newMetamodelType,
    },
  };

  return {
    ...newState,
    hasChanges: !isEqual(
      state.persistedDecomposedMetamodel,
      newDecomposedMetamodel
    ),
    decomposedMetamodel: newDecomposedMetamodel,
  };
};

export const handleDeleteMetamodelType = (
  state: MetamodelEditorState,
  { id: metamodelTypeId }: { id: ArdoqId }
): MetamodelEditorState => {
  const { decomposedMetamodel } = state;

  // Delete the modelType and its children's entries from hierarchy and metamodelTypes
  const newDecomposedMetamodel = deleteModelTypeWithItsChildren(
    decomposedMetamodel,
    metamodelTypeId
  );

  // Get the new Selected ModelType ID
  const parentId = getParentId(state, metamodelTypeId);
  const newSelectedId = newDecomposedMetamodel.metamodelTypes[
    state.selectedMetamodelTypeId || ''
  ]
    ? state.selectedMetamodelTypeId
    : parentId;

  // reset error if the previous selected is removed.
  let editorError = state.editorError;
  if (
    state.selectedMetamodelTypeId &&
    !newDecomposedMetamodel.metamodelTypes[state.selectedMetamodelTypeId] &&
    editorError?.message
  ) {
    editorError = { message: null };
  }

  return {
    ...state,
    hasChanges: !isEqual(
      state.persistedDecomposedMetamodel,
      newDecomposedMetamodel
    ),
    selectedMetamodelTypeId: newSelectedId,
    editorError,
    decomposedMetamodel: newDecomposedMetamodel,
  };
};

export const handleAddMetamodelType = (
  state: MetamodelEditorState,
  { name, parent }: { name: string; parent?: ArdoqId }
): MetamodelEditorState => {
  const {
    decomposedMetamodel: { childrenIndexes, metamodelTypes },
  } = state;
  const parentId = parent || ROOT_ID;
  const parentNode = metamodelTypes[parentId];
  const parentHierarchy = childrenIndexes[parentId];
  if (!parentHierarchy) {
    return state;
  }

  const newType: MetamodelType = getNewMetamodelType({
    name: name.trim(),
    level: (parentNode?.level ?? 0) + 1,
  });

  const newDecomposedMetamodel = {
    childrenIndexes: {
      ...childrenIndexes,
      [parentId]: [...parentHierarchy!, newType.id],
      [newType.id]: [],
    },
    metamodelTypes: {
      ...metamodelTypes,
      [newType.id]: newType,
    },
  };

  return {
    ...state,
    hasChanges: !isEqual(
      state.persistedDecomposedMetamodel,
      newDecomposedMetamodel
    ),
    decomposedMetamodel: newDecomposedMetamodel,
  };
};

export const handleSetIsFlexible = (
  state: MetamodelEditorState,
  payload: { is: boolean }
) => ({
  ...state,
  hasChanges: payload.is !== state.isFlexible,
  isFlexible: payload.is,
});

export const handleSetSelectedMetamodelType = (
  state: MetamodelEditorState,
  { id }: Partial<ByIdMetamodelTypePayload>
): MetamodelEditorState => {
  const { childrenIndexes, metamodelTypes } = state.decomposedMetamodel;

  const rollbackSelectionId = childrenIndexes[ROOT_ID][0];
  const rollbackMetamodelType = metamodelTypes[rollbackSelectionId];

  const newId = id || state.selectedMetamodelTypeId || '';
  const selectedMetamodelType = metamodelTypes[newId] || rollbackMetamodelType;

  return {
    ...state,
    selectedMetamodelTypeId: selectedMetamodelType?.id,
    selectedMetamodelType: selectedMetamodelType,
  };
};
/* ********************************************************************************
 ------------------------------------  Helpers  -----------------------------------
 **********************************************************************************/

const getNewMetamodelType = (
  properties?: Partial<MetamodelType>
): MetamodelType => {
  const newType = {
    children: {},
    color: null,
    description: null,
    icon: null,
    id: generateModelTypeId(),
    image: null,
    level: 1,
    name: '',
    returnsValue: false,
    shape: null,
    standard: DEFAULT_STANDARD,
    ...properties,
  };
  return newType;
};

const generateModelTypeId = (offset = 0) => {
  // Offset makes sure we can generate multiple type-id's that are surely unique
  return `p${currentTimestamp() - offset + Math.round(Math.random() * 10000)}`;
};

const updateMetamodelTypeFromAPayload = (
  metamodelType: MetamodelType,
  payload: Partial<MetamodelType>
): MetamodelType => {
  let changes = { ...payload };
  if (changes.icon || changes.image || changes.shape) {
    changes = {
      ...changes,
      standard: null,
      // 'none' is not a value of metamodelType icons, but it is used to display in selection
      ...(changes.icon
        ? {
            icon: changes.icon === 'none' ? null : changes.icon,
          }
        : undefined),
    };
  }

  if (changes.standard && changes.standard !== DEFAULT_STANDARD) {
    const preset = getStandard(payload.standard as string);
    changes = preset
      ? {
          ...changes,
          image: null,
          color: preset?.color ?? null,
          icon: preset?.icon ?? null,
          shape: preset?.shapeId ?? null,
        }
      : changes;
  }

  return { ...metamodelType, ...changes };
};

const getParentId = (
  { decomposedMetamodel: { childrenIndexes } }: MetamodelEditorState,
  metamodelTypeId: ArdoqId
): ArdoqId => {
  const parentId = Object.entries(childrenIndexes).find(
    ([, searchMetamodelTypeIds]) =>
      searchMetamodelTypeIds.includes(metamodelTypeId)
  )?.[0];
  return parentId || ROOT_ID;
};

const deleteModelTypeWithItsChildren = (
  { childrenIndexes, metamodelTypes }: DecomposedMetamodel,
  deletedMetamodelTypeId: ArdoqId
): DecomposedMetamodel => {
  const childrenTuples = Object.entries(childrenIndexes);

  const idsToRemove = new Set([
    deletedMetamodelTypeId,
    ...getDescendants(childrenIndexes, deletedMetamodelTypeId),
  ]);

  const newChildrenTuples = childrenTuples
    .filter(([id]) => !idsToRemove.has(id))
    .map(([id, childrenIds]) => {
      const newChildrenIds = childrenIds.includes(deletedMetamodelTypeId)
        ? childrenIds.filter(id => id !== deletedMetamodelTypeId)
        : childrenIds;
      return [id, newChildrenIds];
    });

  return {
    childrenIndexes: Object.fromEntries(newChildrenTuples),
    metamodelTypes: Object.fromEntries(
      Object.entries(metamodelTypes).filter(([id]) => !idsToRemove.has(id))
    ),
  };
};
